import { useEffect, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { getAuth, signOut } from 'firebase/auth';
import { getLocalStorage } from 'utils/storage';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useGlobal } from './context/global/GlobalContext';
import { RecaptchaProvider } from 'context/RecaptchaContext';
import { useAPICacheContext } from 'context/APICacheContext/APICacheContext';
import PreviewHandler from 'components/PreviewHandler/PreviewHandler';
import LoadingIndicator from 'components/atomic/LoadingIndicator';
import ThemedGlobalStyle from 'components/ThemedGlobalStyle';
import SuccessDrawer from 'components/drawers/SuccessDrawer';
import AppContainer from 'components/layout/AppContainer';
import CartDrawer from 'components/drawers/CartDrawer';
import Toast from 'components/atomic/Toast';
import GlobalStyle from 'styles/global';
import envVars from './env-vars';
import Routes from './routes';
import 'assets/main.css';

export default function BrijConsumerApp() {
  const { appZoom } = useGlobal();
  const { invalidateCache } = useAPICacheContext();
  const { globalLoading } = useGlobal();

  useEffect(() => {
    const previewModeListener = (event: MessageEvent) => {
      if (event?.data?.type === 'enablePreview') {
        (window as any).mode = 'preview';
        getLocalStorage().clear();
      }
      if (event?.data?.type === 'enableAdminRegistration') {
        (window as any).mode = 'adminRegistration';
        (window as any).adminRegistrationData = event?.data?.data || {};
        getLocalStorage().clear();
        try {
          sessionStorage.clear();
        } catch {
          /* ignore */
        }
        invalidateCache();
        void (async () => {
          try {
            await signOut(getAuth());
          } catch {
            /* Firebase may not be initialized yet */
          }
          window.dispatchEvent(
            new CustomEvent('adminRegistrationReady', {
              detail: event?.data?.data || {},
            })
          );
        })();
      }
    };

    window.addEventListener(
      'message', // listen for messages from the parent
      previewModeListener
    );

    return () => {
      (window as any).mode = '';
      window.removeEventListener('message', previewModeListener);
    };
  }, [invalidateCache]);

  useEffect(() => {
    const productId = getLocalStorage().getItem('currentProductModuleId');
    if (!productId) invalidateCache();
  }, []);

  return (
    <RecaptchaProvider>
      <HelmetProvider>
        <Helmet>
          <link rel='icon' href='/logobrij.png' />
        </Helmet>
        <BrowserRouter basename={envVars.PUBLIC_URL}>
          {globalLoading && (
            <div className='w-full h-full flex fixed z-50 bg-white opacity-50'>
              <LoadingIndicator />
            </div>
          )}
          <PreviewHandler />
          <AppContainer zoom={appZoom}>
            <GlobalStyle />
            <ThemedGlobalStyle />
            <SuccessDrawer />
            <CartDrawer />
            <Suspense
              fallback={
                <div className='w-full h-full'>
                  <LoadingIndicator />
                </div>
              }
            >
              <Routes />
            </Suspense>
            <Toast />
          </AppContainer>
        </BrowserRouter>
      </HelmetProvider>
    </RecaptchaProvider>
  );
}
